/**
 * Adaptive difficulty for the ear trainer — Bayesian threshold estimation.
 *
 * WHAT IT REPORTS: the boost/cut size (in dB) at which the listener still
 * identifies the band reliably. That number is the whole point — a score
 * ("18/20") depends on how hard we happened to make the questions, so it is
 * not comparable across sessions or people. A threshold in dB is.
 *
 * WHY BAYESIAN AND NOT A STAIRCASE. The obvious implementation is an up-down
 * staircase averaged over reversals. It was measured against simulated
 * listeners of known threshold (tests/scratch/staircase-probe.mjs) and it is
 * BIASED: +1.2..+2.2 dB on a 2-alternative task and +2.5..+3.6 dB when the
 * listener picks one of ten bands, and it never reached ±1.5 dB accuracy even
 * at 50 trials. Root cause: a staircase converges on "75 % correct", and with
 * a 1-in-10 guess floor the 75 %-correct point sits well above the threshold.
 * It converges honestly on the wrong quantity.
 *
 * This estimator models the guess floor (1/alternatives) and the lapse rate
 * explicitly, so the number it converges on is the number we display. Same
 * simulated listeners (tests/scratch/bayes-threshold-probe.mjs): bias within
 * ±0.25 dB on the 10-band task, and 79-100 % of runs inside ±1.5 dB after
 * only 20 questions.
 *
 * A useful consequence, which drove the UI: the 10-band "which band" task is
 * a BETTER measuring instrument than a 2-alternative A/B, because a correct
 * answer out of ten carries far more information than one out of two. The
 * main training mode is therefore also the accurate one; A/B is a warm-up,
 * not the meter.
 *
 * ES2019-safe (no ?., ??, class fields) — this file is served raw.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    root.EarTrainerAdaptive = api;
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var GRID_MIN = 0.25;
    var GRID_MAX = 20;
    var GRID_N = 160;

    var GRID = [];
    for (var gi = 0; gi < GRID_N; gi++) {
        GRID.push(GRID_MIN + (GRID_MAX - GRID_MIN) * gi / (GRID_N - 1));
    }

    /* Gains we are allowed to present. Coarse at the top (nobody needs 17 dB
       vs 18 dB) and fine near the bottom where real thresholds live. */
    var GAINS = [0.5, 0.75, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 15, 18];

    var LAPSE = 0.03;      // attention slips — a real listener sometimes misclicks
    var SLOPE = 1.6;       // assumed psychometric slope in dB; deliberately fixed

    /**
     * P(correct | presented gain, true threshold) for an N-alternative task.
     * Floor at 1/alternatives (pure guessing), ceiling at 1 - LAPSE.
     */
    function pCorrect(gain, threshold, alternatives) {
        var guess = 1 / Math.max(2, alternatives);
        var f = 1 / (1 + Math.exp(-(gain - threshold) / SLOPE));
        return guess + (1 - guess - LAPSE) * f;
    }

    /** Weakly informative prior: log-normal-ish centred near 6 dB, wide. */
    function makePrior() {
        var p = new Float64Array(GRID_N);
        var sum = 0;
        for (var i = 0; i < GRID_N; i++) {
            var t = GRID[i];
            var z = (Math.log(t) - Math.log(6)) / 0.9;
            p[i] = Math.exp(-0.5 * z * z) / t;
            sum += p[i];
        }
        for (var j = 0; j < GRID_N; j++) p[j] /= sum;
        return p;
    }

    function create(opts) {
        opts = opts || {};
        var alternatives = opts.alternatives || 10;
        var minGain = typeof opts.minGain === 'number' ? opts.minGain : 0.5;
        var maxGain = typeof opts.maxGain === 'number' ? opts.maxGain : 18;

        var state = {
            posterior: makePrior(),
            trials: 0,
            correct: 0,
            history: []
        };

        function mean() {
            var m = 0;
            for (var i = 0; i < GRID_N; i++) m += GRID[i] * state.posterior[i];
            return m;
        }

        function sd() {
            var m = mean(), v = 0;
            for (var i = 0; i < GRID_N; i++) {
                var d = GRID[i] - m;
                v += d * d * state.posterior[i];
            }
            return Math.sqrt(v);
        }

        /**
         * Credible interval. The raw 68 % interval is over-confident when the
         * assumed slope does not match the listener (measured coverage fell to
         * ~55 % for the shallowest simulated listeners), so it is widened by
         * `inflate` before being shown. We would rather say "3-6 dB" and be
         * right than say "4.1-4.4 dB" and be wrong.
         */
        function interval(mass, inflate) {
            mass = mass || 0.68;
            inflate = typeof inflate === 'number' ? inflate : 1.35;
            var lo = (1 - mass) / 2, hi = 1 - lo;
            var c = 0, a = GRID[0], b = GRID[GRID_N - 1], gotA = false;
            for (var i = 0; i < GRID_N; i++) {
                c += state.posterior[i];
                if (!gotA && c >= lo) { a = GRID[i]; gotA = true; }
                if (c >= hi) { b = GRID[i]; break; }
            }
            var m = mean();
            return [
                Math.max(GRID_MIN, m - (m - a) * inflate),
                Math.min(GRID_MAX, m + (b - m) * inflate)
            ];
        }

        /** Entropy of a normalised distribution. */
        function entropyOf(p) {
            var h = 0;
            for (var i = 0; i < GRID_N; i++) if (p[i] > 1e-12) h -= p[i] * Math.log(p[i]);
            return h;
        }

        /**
         * Next question's gain: the one whose answer is expected to shrink the
         * posterior the most (minimum expected entropy). Costs ~15 x 160 x 2
         * float ops per question — nothing.
         */
        function nextGain() {
            var best = GAINS[0], bestH = Infinity;
            var postC = new Float64Array(GRID_N), postW = new Float64Array(GRID_N);
            for (var gi2 = 0; gi2 < GAINS.length; gi2++) {
                var g = GAINS[gi2];
                if (g < minGain || g > maxGain) continue;
                var pc = 0, sC = 0, sW = 0, i;
                for (i = 0; i < GRID_N; i++) {
                    var pr = pCorrect(g, GRID[i], alternatives);
                    pc += state.posterior[i] * pr;
                    postC[i] = state.posterior[i] * pr; sC += postC[i];
                    postW[i] = state.posterior[i] * (1 - pr); sW += postW[i];
                }
                var hC = 0, hW = 0;
                for (i = 0; i < GRID_N; i++) {
                    if (sC > 0) { var vc = postC[i] / sC; if (vc > 1e-12) hC -= vc * Math.log(vc); }
                    if (sW > 0) { var vw = postW[i] / sW; if (vw > 1e-12) hW -= vw * Math.log(vw); }
                }
                var expH = pc * hC + (1 - pc) * hW;
                if (expH < bestH) { bestH = expH; best = g; }
            }
            return best;
        }

        function update(gain, wasCorrect) {
            var next = new Float64Array(GRID_N);
            var s = 0, i;
            for (i = 0; i < GRID_N; i++) {
                var pr = pCorrect(gain, GRID[i], alternatives);
                next[i] = state.posterior[i] * (wasCorrect ? pr : (1 - pr));
                s += next[i];
            }
            if (s > 0) { for (i = 0; i < GRID_N; i++) next[i] /= s; }
            else { for (i = 0; i < GRID_N; i++) next[i] = 1 / GRID_N; }
            state.posterior = next;
            state.trials++;
            if (wasCorrect) state.correct++;
            state.history.push({ gain: gain, correct: !!wasCorrect });
            return next;
        }

        /**
         * Have we measured enough to show a number? Two guards: a minimum
         * number of questions (a posterior can look tight after three lucky
         * answers) and a posterior spread small enough to be worth printing.
         */
        function isSettled() {
            return state.trials >= 12 && sd() < 1.4;
        }

        function serialise() {
            return { trials: state.trials, correct: state.correct, mean: mean(), sd: sd() };
        }

        function reset() {
            state.posterior = makePrior();
            state.trials = 0;
            state.correct = 0;
            state.history = [];
        }

        return {
            nextGain: nextGain,
            update: update,
            mean: mean,
            sd: sd,
            interval: interval,
            isSettled: isSettled,
            trials: function () { return state.trials; },
            correct: function () { return state.correct; },
            history: function () { return state.history.slice(); },
            serialise: serialise,
            reset: reset,
            setAlternatives: function (n) { alternatives = n; },
            setGainRange: function (lo, hi) { minGain = lo; maxGain = hi; }
        };
    }

    /**
     * Exact right-tail binomial p-value — "could this score have come from
     * guessing?". Ported from the same helper audio-comparator uses for its
     * blind A/B, so the two tools answer that question identically.
     */
    function binomialPValue(correct, total, pGuess) {
        if (!total || correct <= 0) return 1;
        pGuess = pGuess || 0.5;
        var logFact = [0];
        for (var i = 1; i <= total; i++) logFact[i] = logFact[i - 1] + Math.log(i);
        var sum = 0;
        for (var k = correct; k <= total; k++) {
            var logC = logFact[total] - logFact[k] - logFact[total - k];
            sum += Math.exp(logC + k * Math.log(pGuess) + (total - k) * Math.log(1 - pGuess));
        }
        return Math.min(1, Math.max(0, sum));
    }

    return {
        create: create,
        binomialPValue: binomialPValue,
        GAINS: GAINS,
        _pCorrect: pCorrect
    };
}));
